<script setup>
import { computed, inject } from 'vue';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useVueFlow,
} from '@vue-flow/core';

const props = defineProps({
  id: { type: String, required: true },
  source: { type: String, required: true },
  target: { type: String, required: true },
  sourceX: { type: Number, required: true },
  sourceY: { type: Number, required: true },
  targetX: { type: Number, required: true },
  targetY: { type: Number, required: true },
  sourcePosition: { type: String, required: true },
  targetPosition: { type: String, required: true },
  markerEnd: { type: String, default: '' },
  style: { type: Object, default: () => ({}) },
});

defineOptions({ inheritAttrs: false });

const openNodePicker = inject('openNodePicker');
const { applyEdgeChanges } = useVueFlow();

const pathData = computed(() => {
  const [d, lx, ly] = getBezierPath({
    sourceX: props.sourceX,
    sourceY: props.sourceY,
    targetX: props.targetX,
    targetY: props.targetY,
    sourcePosition: props.sourcePosition,
    targetPosition: props.targetPosition,
  });
  return { d, lx, ly };
});

function onAdd(e) {
  e.stopPropagation();
  openNodePicker({
    edgeId: props.id,
    source: props.source,
    target: props.target,
    x: pathData.value.lx,
    y: pathData.value.ly,
  });
}

function onDelete(e) {
  e.stopPropagation();
  applyEdgeChanges([{ type: 'remove', id: props.id }]);
}
</script>

<template>
  <BaseEdge
    :id="id"
    :path="pathData.d"
    :style="{ strokeWidth: 2, stroke: '#94a3b8', ...style }"
    :marker-end="markerEnd"
  />

  <EdgeLabelRenderer>
    <div
      class="nodrag nopan absolute flex items-center gap-1"
      :style="{
        pointerEvents: 'all',
        transform: `translate(-50%, -50%) translate(${pathData.lx}px, ${pathData.ly}px)`,
      }"
    >
      <button
        type="button"
        class="flex items-center justify-center size-8 rounded-md bg-n-solid-1 text-n-slate-10 hover:bg-n-brand hover:text-white shadow-sm transition-all duration-100"
        @click="onAdd"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>
      <button
        type="button"
        class="flex items-center justify-center size-8 rounded-md bg-n-solid-1 text-n-ruby-9 hover:bg-n-ruby-9 hover:text-white shadow-sm transition-all duration-100"
        @click="onDelete"
      >
        <span class="i-lucide-trash-2 size-4" />
      </button>
    </div>
  </EdgeLabelRenderer>
</template>
